import type { PermissionRequirement } from '@/types/permission'
import { REQUIREMENT_PERMISSIONS } from '@/views/requirement/domain/permissions'

export interface PermissionedRouteCandidate {
  label?: string
  route: string
  permission: PermissionRequirement
}

export const goalRouteCandidates: PermissionedRouteCandidate[] = [
  { label: 'OKR 管理', route: '/goals/okr', permission: 'goal:okr:view' },
  { label: '周目标', route: '/goals/weekly', permission: 'goal:record:view' },
  { label: '日目标', route: '/goals/daily', permission: 'goal:record:view' },
  { label: '目标仪表盘', route: '/goals/dashboard', permission: 'goal:record:view' },
]

export const productManagementRouteCandidates: PermissionedRouteCandidate[] = [
  { label: '产品列表', route: '/products/overview', permission: 'product:overview:view' },
  { label: '工作项管理', route: '/products/work-pool', permission: 'product:work_pool:view' },
  { label: '产品蓝图', route: '/products/blueprint', permission: 'product:blueprint:view' },
  { label: '产品成熟度', route: '/products/maturity', permission: 'product:maturity:view' },
  { label: '使用率', route: '/products/usage', permission: 'product:usage_analysis:view' },
  { label: '满意度', route: '/products/satisfaction', permission: 'product:satisfaction_analysis:view' },
]

export const projectRouteCandidates: PermissionedRouteCandidate[] = [
  { label: '项目管理', route: '/projects', permission: 'project:record:view' },
  { label: '风险', route: '/projects/risks', permission: 'project:risk:view' },
]

export const projectTopNavTabs = projectRouteCandidates

export const requirementRouteCandidates: PermissionedRouteCandidate[] = [
  { label: '工作台', route: '/requirements', permission: REQUIREMENT_PERMISSIONS.workspace.view },
  { label: '需求', route: '/requirements/records', permission: REQUIREMENT_PERMISSIONS.record.view },
  { label: '任务', route: '/requirements/tasks', permission: REQUIREMENT_PERMISSIONS.task.view },
  { label: '缺陷', route: '/requirements/bugs', permission: REQUIREMENT_PERMISSIONS.bug.view },
  { label: '迭代', route: '/requirements/iterations', permission: REQUIREMENT_PERMISSIONS.iteration.view },
  { label: '分析', route: '/requirements/analytics', permission: REQUIREMENT_PERMISSIONS.analytics.view },
]

export const virtualEmployeeRouteCandidates: PermissionedRouteCandidate[] = [
  { label: '价值驾驶舱', route: '/virtual-employee/dashboard', permission: 'virtual_employee:dashboard:view' },
  { label: '配置工作台', route: '/virtual-employee/config', permission: 'virtual_employee:config:view' },
]

/**
 * 采集中心的全部可落地页面，用于模块可访问性判定与默认落地路由。
 *
 * <p>顺序即默认落地优先级：数据源与采集实例排在后面，它们已降级为二级页面
 * （数据源在 Agent 详情内、采集实例从异常入口进），不该成为进模块后的第一眼。
 * 但两者仍留在候选里——只有数据源权限的角色要靠它进得来。
 */
export const collectRouteCandidates: PermissionedRouteCandidate[] = [
  { label: '工作台', route: '/collect/dashboard', permission: 'collect:center:view' },
  { label: 'Agent', route: '/collect/agents', permission: 'collect:agent:view' },
  { label: '指标', route: '/collect/metrics', permission: 'collect:metric:view' },
  { label: '数据源', route: '/collect/sources', permission: 'collect:source:view' },
  { label: '采集实例', route: '/collect/instances', permission: 'collect:metric:view' },
]

/**
 * 采集中心顶栏 tab。
 *
 * <p>只有三个：数据源已并入 Agent 详情（source 的运行时归属就是 Agent），
 * 采集实例是指标与 Agent 的执行记录切面，从工作台异常入口或详情页"查看全部"进。
 * 顶栏与 {@link collectRouteCandidates} 分开正是因为二级页仍需要参与权限与落地路由计算。
 */
export const collectTopNavTabs: PermissionedRouteCandidate[] = [
  { label: '工作台', route: '/collect/dashboard', permission: 'collect:center:view' },
  { label: 'Agent', route: '/collect/agents', permission: 'collect:agent:view' },
  { label: '指标', route: '/collect/metrics', permission: 'collect:metric:view' },
]

/** 只有数据源权限、进不了 Agent 页的角色的兜底顶栏入口，否则数据源对它彻底不可达。 */
export const collectSourceFallbackTab: PermissionedRouteCandidate = {
  label: '数据源',
  route: '/collect/sources',
  permission: 'collect:source:view',
}

export const incidentRouteCandidates: PermissionedRouteCandidate[] = [
  { label: '故障列表', route: '/incidents', permission: 'incident:record:view' },
  { label: '故障复盘', route: '/incidents/resolved', permission: 'incident:record:view' },
  { label: '故障配置', route: '/incidents/config', permission: 'incident:config:view' },
  { label: '催办规则', route: '/incidents/config?tab=rules', permission: 'incident:reminder_rule:view' },
  { label: '催办日志', route: '/incidents/config?tab=logs', permission: 'incident:reminder_log:view' },
]

export const feedbackRouteCandidates: PermissionedRouteCandidate[] = [
  { label: '反馈列表', route: '/feedback/items', permission: 'feedback:item:view' },
  { label: '接入应用', route: '/feedback/apps', permission: 'feedback:app:view' },
]

export const efficiencyRouteCandidates: PermissionedRouteCandidate[] = [
  { label: '人效总览', route: '/efficiency', permission: 'efficiency:overview:view' },
  { label: '赛道人效排名', route: '/efficiency/rankings', permission: 'efficiency:ranking:view' },
]

export const incidentTopNavTabs: PermissionedRouteCandidate[] = [
  { label: '故障列表', route: '/incidents', permission: 'incident:record:view' },
  { label: '故障复盘', route: '/incidents/resolved', permission: 'incident:record:view' },
  {
    label: '故障配置',
    route: '/incidents/config',
    permission: {
      any: [
        'incident:config:view',
        'incident:reminder_rule:view',
        'incident:reminder_log:view',
      ],
    },
  },
]

export const toAnyPermission = (candidates: PermissionedRouteCandidate[]): PermissionRequirement => ({
  any: Array.from(new Set(candidates.flatMap(candidate => extractPermissionCodes(candidate.permission)))),
})

function extractPermissionCodes(requirement: PermissionRequirement): string[] {
  if (typeof requirement === 'string') return [requirement]
  if (Array.isArray(requirement)) return requirement
  return [
    ...(requirement.any ?? []),
    ...(requirement.all ?? []),
  ]
}
